'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Upload, Trash2, Star, Loader2, Save, Image as ImageIcon } from 'lucide-react';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  caption: string;
  isPrimary: boolean;
}

const initialImages: GalleryImage[] = [
{ id: 'gal-mgmt-001', src: "https://images.unsplash.com/photo-1566489385366-a48be9217ce5", alt: 'Construction workers on scaffolding during a home extension build', caption: 'Two-storey extension, Bury St Edmunds', isPrimary: true },
{ id: 'gal-mgmt-002', src: "https://img.rocket.new/generatedImages/rocket_gen_img_19cba9158-1773093032586.png", alt: 'Finished modern kitchen with island and bi-fold doors', caption: 'Kitchen renovation, Newmarket', isPrimary: false },
{ id: 'gal-mgmt-003', src: "https://img.rocket.new/generatedImages/rocket_gen_img_104bbb00e-1777157898544.png", alt: 'New build detached house with brick exterior and garage', caption: 'New build, Haverhill', isPrimary: false },
{ id: 'gal-mgmt-004', src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca4a3d49-1772200826589.png", alt: 'Converted loft bedroom with Velux windows and timber flooring', caption: 'Loft conversion, Mildenhall', isPrimary: false }];


export default function GalleryTab() {
  const [images, setImages] = useState<GalleryImage[]>(initialImages);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const maxImages = 12;

  const setPrimary = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
    toast.success('Primary image updated');
  };

  const deleteImage = (id: string) => {
    setDeletingId(id);
    setTimeout(() => {
      setImages((prev) => prev.filter((img) => img.id !== id));
      setDeletingId(null);
      toast.success('Image removed from gallery');
    }, 300);
  };

  const updateCaption = (id: string, caption: string) => {
    setImages((prev) => prev.map((img) => img.id === id ? { ...img, caption } : img));
  };

  const handleSave = async () => {
    setIsLoading(true);
    // Backend integration point: PUT /api/businesses/{id}/gallery
    await new Promise((r) => setTimeout(r, 1000));
    toast.success('Gallery saved successfully');
    setIsLoading(false);
  };

  return (
    <div>
      {/* Upload area */}
      <div className="card-surface p-6 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-base text-foreground">Photo Gallery</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {images.length} / {maxImages} images uploaded. Max 5MB per image, JPG or PNG.
            </p>
          </div>
        </div>

        {images.length < maxImages &&
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/40 transition-colors cursor-pointer mb-5">
            {/* Backend integration point: file upload handler */}
            <Upload size={28} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-semibold text-foreground mb-1">
              Drag and drop images here
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              or click to browse your files
            </p>
            <button type="button" className="btn-secondary text-sm">
              <Upload size={13} />
              Choose Files
            </button>
          </div>
        }

        {/* Image grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((img) =>
          <div
            key={img.id}
            className={`relative group border-2 rounded overflow-hidden transition-all duration-300 ${
            img.isPrimary ? 'border-primary' : 'border-border'} ${
            deletingId === img.id ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            
              {/* Primary badge */}
              {img.isPrimary &&
            <div className="absolute top-2 left-2 z-10 bg-primary text-primary-foreground text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Star size={10} />
                  Primary
                </div>
            }

              <div className="aspect-video bg-muted overflow-hidden">
                <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              
              </div>

              {/* Hover actions */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                {!img.isPrimary &&
              <button
                type="button"
                onClick={() => setPrimary(img.id)}
                className="bg-white/90 text-foreground p-2 rounded hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Set as primary image"
                title="Set as primary photo">
                
                    <Star size={14} />
                  </button>
              }
                <button
                type="button"
                onClick={() => deleteImage(img.id)}
                className="bg-white/90 text-foreground p-2 rounded hover:bg-negative hover:text-white transition-colors"
                aria-label="Delete image"
                title="Remove from gallery">
                
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Caption */}
              <div className="p-2 bg-card border-t border-border">
                <input
                type="text"
                value={img.caption}
                onChange={(e) => updateCaption(img.id, e.target.value)}
                className="w-full text-xs text-muted-foreground bg-transparent border-none outline-none focus:text-foreground placeholder:text-muted-foreground/50"
                placeholder="Add a caption..."
                aria-label="Image caption" />
              
              </div>
            </div>
          )}

          {/* Empty slot indicators */}
          {Array.from({ length: Math.max(0, Math.min(4, maxImages - images.length)) }).map((_, i) =>
          <div
            key={`empty-slot-${i + 1}`}
            className="border-2 border-dashed border-border rounded aspect-video flex items-center justify-center bg-muted/20">
            
              <ImageIcon size={20} className="text-muted-foreground/30" />
            </div>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 -mx-6 flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          Images are reviewed before appearing on your public listing.
        </p>
        <div className="flex gap-3">
          <button type="button" className="btn-secondary text-sm">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="btn-primary text-sm"
            style={{ minWidth: 140 }}>
            
            {isLoading ? <><Loader2 size={14} className="animate-spin" />Saving...</> : <><Save size={14} />Save Gallery</>}
          </button>
        </div>
      </div>
    </div>);

}